/**
 * Sondos journey insights — v3 (2026-10-11).
 * Plan: .cursor/plans/2026-10-11/journey-bi-implementation.plan.md
 *
 * Two outputs, one rule.
 *  1. A per-visit summary saved on the ORDER as cart attributes. It rides
 *     inside the checkout POST itself (hidden `attributes[...]` fields written
 *     synchronously on the checkout tap) — the tap is never cancelled, delayed
 *     or replayed. There are no background /cart/update.js syncs any more:
 *     the hand-off already carries everything, so tracking never mutates the
 *     cart while she shops.
 *  2. Feature events via Shopify.analytics.publish('sondos_*'). Shopify's
 *     Customer Events layer batches them and applies consent; a custom pixel
 *     forwards them to GA4. Publishing is a synchronous, in-memory call.
 *
 * The rule: tracking only OBSERVES. Every listener is passive and none calls
 * preventDefault/stopPropagation. If anything here throws, shopping goes on
 * unchanged — the shop just loses that one event.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'sondos_journey_v2';
  var SRC_KEY = 'sondos_add_src_v1';
  var FIRST_KEY = 'sondos_first_v1';
  var VIA_KEY = 'sondos_via_v1';
  var WINDOW_MS = 30 * 60 * 1000;
  var SRC_TTL_MS = 21 * 24 * 60 * 60 * 1000;
  var FIRST_TTL_MS = 90 * 24 * 60 * 60 * 1000;
  var MAX_PAGES = 8;
  var MAX_PRODUCTS = 8;
  var MAX_COLLECTIONS = 6;
  var MAX_SEARCHES = 4;
  var MAX_SRC = 30;
  var MAX_ATTR = 240;
  var PENDING_MS = 15000;
  // «مكمّلات» decision context carried from a card click to the product page (2026-10-19): per tab (sessionStorage)
  var FBT_CTX_KEY = 'sondos_fbt_ctx_v1';
  var FBT_CTX_TTL_MS = 30 * 60 * 1000;

  var state = null;
  var pageType = '';
  var pending = [];
  var publishQueue = [];

  // ── helpers ──────────────────────────────────────────────────────────────
  function now() { return Date.now(); }

  function clip(str, max) {
    str = String(str == null ? '' : str);
    return str.length <= max ? str : str.slice(0, max - 1) + '…';
  }

  function decodePart(part) {
    try { return decodeURIComponent(part); } catch (e) { return part; }
  }

  function cleanPath(p) {
    return decodePart((p || '/').replace(/\/+$/, '') || '/');
  }

  function pushUniqueTail(list, value, max) {
    if (!value) return list || [];
    var next = (list || []).filter(function (x) { return x !== value; });
    next.push(value);
    return next.length > max ? next.slice(next.length - max) : next;
  }

  function pushUnique(list, value) {
    list = list || [];
    if (value && list.indexOf(value) === -1) list.push(value);
    return list;
  }

  function readJSON(store, key) {
    try { var raw = store.getItem(key); return raw ? JSON.parse(raw) : null; } catch (e) { return null; }
  }

  function writeJSON(store, key, value) {
    try { store.setItem(key, JSON.stringify(value)); } catch (e) { /* private mode / quota: in-memory only */ }
  }

  function readQuery() {
    try { return new URLSearchParams(location.search || ''); } catch (e) { return null; }
  }

  function detectDevice() {
    try { if (window.matchMedia && window.matchMedia('(max-width: 749px)').matches) return 'mobile'; } catch (e) {}
    return 'desktop';
  }

  function readTimeZone() {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch (e) { return ''; }
  }

  function readLanguages() {
    try {
      var list = navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language || ''];
      return Array.prototype.slice.call(list, 0, 4).join(',');
    } catch (e) { return ''; }
  }

  function readPageType() {
    var m = (document.body && document.body.className || '').match(/template-([a-z0-9_-]+)/);
    return m ? m[1] : '';
  }

  // Shopify's Customer Privacy API, when loaded, has the last word.
  function consentOk() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.analyticsProcessingAllowed === 'function') return cp.analyticsProcessingAllowed() !== false;
    } catch (e) {}
    return true;
  }

  // Fire-and-forget into Shopify's analytics bus (no network here; Shopify
  // batches). Events published before the bus exists wait in a small queue.
  function publish(name, data) {
    if (!consentOk()) return;
    var payload = data || {};
    payload.page = pageType;
    try {
      var a = window.Shopify && window.Shopify.analytics;
      if (a && typeof a.publish === 'function') { a.publish(name, payload); return; }
    } catch (e) { return; }
    if (publishQueue.length < 20) publishQueue.push([name, payload]);
  }

  function flushPublishQueue() {
    if (!publishQueue.length) return;
    try {
      var a = window.Shopify && window.Shopify.analytics;
      if (!a || typeof a.publish !== 'function') return;
      var q = publishQueue; publishQueue = [];
      q.forEach(function (item) { try { a.publish(item[0], item[1]); } catch (e) {} });
    } catch (e) {}
  }

  // ── where on the page did a click / add come from ────────────────────────
  function sectionKeyOf(el) {
    var s = el && el.closest && el.closest('[id^="shopify-section-"]');
    if (!s) return '';
    var id = s.id.replace('shopify-section-', '');
    var cut = id.lastIndexOf('__');
    return (cut > -1 ? id.slice(cut + 2) : id).slice(0, 40);
  }

  function addMoreTab() {
    var t = document.querySelector('dialog.sca .sca__tab[aria-selected="true"]');
    return t ? clip((t.textContent || '').replace(/\s+/g, ' ').trim(), 24) : '';
  }

  var modalSource = '';
  var modalFbt = null;

  // ── «مكمّلات» v6 measurement (2026-10-18): which run, page (anchor), visible slot and stored position a card is.
  // Product ids and a run id only — no customer data. Label ≤ 40 chars: complementary:<slot>@<run8>@<anchor>
  // (the collector groups on the part before ':' so dashboards keep «مكمّلات المنتج»).
  // 2026-10-19: the label is for grouping only. The decision itself travels as a structured context (full run,
  // anchor, product / variant, slot, position, view id) from view → click → product page → confirmed add.
  function fbtViewId(list) {
    var v = list.getAttribute('data-sji-view');
    if (!v) { v = now().toString(36) + Math.random().toString(36).slice(2, 7); list.setAttribute('data-sji-view', v); }
    return v;
  }

  function fbtOf(el) {
    var slide = el && el.closest && el.closest('[data-spc-slot]');
    var list = slide && slide.closest('.spc[data-spc-anchor]');
    if (!list) return null;
    var vEl = slide.querySelector('form [name="id"]');
    return {
      run: list.getAttribute('data-spc-run') || 'unattributed',
      anchor: list.getAttribute('data-spc-anchor') || '',
      slot: slide.getAttribute('data-spc-slot') || '',
      pos: slide.getAttribute('data-spc-pos') || '',
      product_id: slide.getAttribute('data-spc-pid') || '',
      variant_id: vEl ? String(vEl.value || '') : '',
      view_id: fbtViewId(list)
    };
  }

  function fbtLabel(f) { return 'complementary:' + f.slot + '@' + String(f.run).slice(0, 8) + '@' + f.anchor; }

  function fbtPayload(action, f, extra) {
    var d = { action: action, run: f.run, anchor: f.anchor, slot: f.slot, position: f.pos, product_id: f.product_id, view_id: f.view_id };
    if (f.variant_id) d.variant_id = f.variant_id;
    for (var k in extra) if (Object.prototype.hasOwnProperty.call(extra, k)) d[k] = extra[k];
    return d;
  }

  // click on a card that leads to its product page: remember THIS decision for that product, in this tab only
  function rememberFbtCtx(handle, f) {
    if (!handle || !f) return;
    var t = now(), m = readJSON(sessionStorage, FBT_CTX_KEY) || {}, out = {};
    m[handle] = { run: f.run, anchor: f.anchor, anchor_path: cleanPath(location.pathname), slot: f.slot, pos: f.pos, product_id: f.product_id, variant_id: f.variant_id, view_id: f.view_id, at: t };
    Object.keys(m).sort(function (a, b) { return m[b].at - m[a].at; }).slice(0, 20).forEach(function (k) { if (t - m[k].at < FBT_CTX_TTL_MS) out[k] = m[k]; });
    writeJSON(sessionStorage, FBT_CTX_KEY, out);
  }

  // on a product page: the remembered decision applies only if it is fresh AND this page was opened from the
  // anchor page that showed it (same-origin referrer) — never another tab's, another anchor's or another product's
  var pdpFbt = null;
  function bindPdpFbt() {
    var handle = productHandleFromHref(location.pathname);
    var c = handle ? (readJSON(sessionStorage, FBT_CTX_KEY) || {})[handle] : null;
    if (!c || !c.at || now() - c.at >= FBT_CTX_TTL_MS) return null;
    var ref = '';
    try { var r = new URL(document.referrer); if (r.origin === location.origin) ref = cleanPath(r.pathname); } catch (e) {}
    return ref && ref === c.anchor_path ? c : null;
  }

  function pdpFbtFor(form) {
    var c = pdpFbt;
    if (!c || now() - c.at >= FBT_CTX_TTL_MS) return null;
    var pid = form && form.querySelector && form.querySelector('[name="product-id"]');
    if (pid && pid.value && String(pid.value) !== String(c.product_id)) return null;
    return { run: c.run, anchor: c.anchor, slot: c.slot, pos: c.pos, product_id: c.product_id, variant_id: '', view_id: c.view_id, via: 'pdp' };
  }

  var attemptSeq = 0;
  var fbtAdded = {};

  // A short, stable label for the component an element sits in.
  function contextOf(el) {
    if (!el || !el.closest) return '';
    if (el.closest('dialog.sca')) return 'add_more:' + addMoreTab();
    var reco = el.closest('[data-sc-reco-card]');
    if (reco) return 'reco:' + (reco.getAttribute('data-sc-reco-position') || '');
    if (el.closest('#sds-search')) return 'search_suggest';
    if (el.closest('.sds-res, .ssz')) return 'search';
    if (el.closest('.arb, #sondos-routine-builder')) return 'arb';
    if (el.closest('quick-add-modal')) return modalSource || 'card';
    var sec = sectionKeyOf(el);
    if (pageType === 'search') return 'search';
    if (pageType === 'collection') return 'collection';
    if (pageType === 'cart') return sec.indexOf('also') > -1 ? 'cart_also' : 'cart';
    if (pageType === 'product') {
      if (el.closest('.spc')) { var fb = fbtOf(el); return fb ? fbtLabel(fb) : 'complementary'; }
      if (el.closest('product-recommendations, .product-recommendations')) return 'related';
      return 'pdp';
    }
    if (pageType === 'index') return sec ? 'home:' + sec : 'home';
    return pageType + (sec ? ':' + sec : '');
  }

  // ── journey state ─────────────────────────────────────────────────────────
  function emptyJourney(ts) {
    return {
      id: Math.random().toString(36).slice(2, 8),
      startedAt: ts,
      lastAt: ts,
      landingPath: '',
      utm: '',
      device: detectDevice(),
      pages: [],
      pagesCount: 0,
      products: [],
      collections: [],
      searches: [],
      sections: [],
      info: [],
      arbStarted: false,
      arbUsed: false,
      arbUses: 0,
      arbAnswers: '',
      arbRoutine: '',
      arbFurthest: '',
      atc: 0,
      addSources: [],
      timeToAtcMin: '',
      promoSeen: false,
      waClicked: false,
      addMoreOpened: 0,
      recoSeen: false,
      fbtSeen: [],
      zone: ''
    };
  }

  function normalize(data, ts) {
    var base = emptyJourney(ts);
    if (!data || typeof data !== 'object') return base;
    Object.keys(base).forEach(function (key) {
      if (typeof data[key] !== 'undefined') base[key] = data[key];
    });
    ['pages', 'products', 'collections', 'searches', 'sections', 'info', 'addSources', 'fbtSeen'].forEach(function (k) {
      if (!Array.isArray(base[k])) base[k] = [];
    });
    if (!base.device) base.device = detectDevice();
    base.lastAt = ts;
    return base;
  }

  var isNewVisit = false;

  function load() {
    var ts = now();
    var data = readJSON(localStorage, STORAGE_KEY);
    if (!data || !data.lastAt || ts - data.lastAt > WINDOW_MS) { isNewVisit = true; return emptyJourney(ts); }
    return normalize(data, ts);
  }

  function save() {
    if (!state) return;
    state.lastAt = now();
    writeJSON(localStorage, STORAGE_KEY, state);
  }

  // Device-local first touch (this browser only — no cross-device identity).
  function firstTouch() {
    var ts = now();
    var f = readJSON(localStorage, FIRST_KEY);
    if (!f || !f.at || ts - f.last > FIRST_TTL_MS) f = { at: ts, utm: state.utm || '', landing: state.landingPath || '', visits: 0, last: ts };
    if (isNewVisit) f.visits = (f.visits || 0) + 1;
    f.last = ts;
    writeJSON(localStorage, FIRST_KEY, f);
    return f;
  }

  // ── add-source map: variant → last source that added it ──────────────────
  function readSrcMap() {
    var m = readJSON(localStorage, SRC_KEY);
    return m && typeof m === 'object' ? m : {};
  }

  function recordSrc(variantId, src) {
    if (!variantId || !src) return;
    var m = readSrcMap();
    m[String(variantId)] = [clip(src, 40), now()];
    var keys = Object.keys(m).sort(function (a, b) { return m[b][1] - m[a][1]; });
    var out = {};
    keys.slice(0, MAX_SRC).forEach(function (k) { if (now() - m[k][1] < SRC_TTL_MS) out[k] = m[k]; });
    writeJSON(localStorage, SRC_KEY, out);
  }

  function srcAttribute() {
    var m = readSrcMap();
    var parts = [];
    Object.keys(m).forEach(function (k) { if (now() - m[k][1] < SRC_TTL_MS) parts.push(k + ':' + m[k][0]); });
    return clip(parts.join(';'), 1000);
  }

  // ── adds: remember the attempt on submit, count it once it succeeded ─────
  function srcGroup(src) { return String(src || '').split(/[:>]/)[0]; }

  function commitAdd(src, variantId, qty, fbt) {
    state.atc += 1;
    state.addSources = pushUnique(state.addSources, srcGroup(src));
    if (state.timeToAtcMin === '' || state.timeToAtcMin == null) {
      state.timeToAtcMin = Math.max(0, Math.round((now() - state.startedAt) / 60000));
    }
    if (variantId) recordSrc(variantId, src);
    save();
    publish('sondos_add', { src: src, variant_id: String(variantId || ''), qty: qty || 1 });
    // a SUCCESSFUL add of a «مكمّلات» pick (commitAdd runs only after the cart confirmed it), once per attempt,
    // with the full decision context — the card's own, or the one carried from the card to this product page
    if (fbt && !fbtAdded[fbt.attempt]) {
      fbtAdded[fbt.attempt] = true;
      publish('sondos_fbt', fbtPayload('add', fbt, { variant_id: String(variantId || fbt.variant_id || ''), qty: qty || 1, via: fbt.via || 'card' }));
    }
  }

  function takePending(variantId) {
    var t = now();
    pending = pending.filter(function (p) { return t - p.at < PENDING_MS; });
    var i = -1;
    if (variantId) {
      for (var j = pending.length - 1; j >= 0; j--) if (pending[j].vid === String(variantId)) { i = j; break; }
    }
    var exact = i > -1, alone = pending.length === 1;
    if (i === -1) i = pending.length - 1;
    if (i < 0) return null;
    var p = pending.splice(i, 1)[0];
    // the old fallback (latest attempt) still feeds sondos_add; a decision context moves only with certainty
    if (p.fbt && (variantId ? !exact : !alone)) p.fbt = null;
    return p;
  }

  function pdpSource() {
    var via = viaForThisProduct();
    return 'pdp' + (via ? '>' + via : '');
  }

  function onSubmitCapture(e) {
    var form = e.target;
    if (!form || form.tagName !== 'FORM') return;
    var action = form.getAttribute('action') || '';
    var submitter = e.submitter || null;

    // AI cart reco: a native POST that reloads /cart — count it now.
    // getAttribute, not form.id: the card buttons are named `id`, which
    // clobbers form.id with the control list.
    if (form.getAttribute('id') === 'sondos-sc-add') {
      var card = submitter && submitter.closest && submitter.closest('[data-sc-reco-card]');
      var pos = card ? card.getAttribute('data-sc-reco-position') || '' : '';
      var vid = submitter && submitter.value;
      commitAdd('reco:' + pos, vid, 1);
      publish('sondos_reco', { action: 'add', position: pos, variant_id: String(vid || '') });
      return;
    }

    if (action.indexOf('/cart/add') > -1) {
      var idEl = form.querySelector('[name="id"]');
      var qEl = form.querySelector('[name="quantity"]');
      var ctx = contextOf(form);
      var fb = null;
      if (ctx.indexOf('complementary:') === 0) { fb = fbtOf(form); if (fb) fb.via = 'card'; }
      else if (ctx === 'pdp') { ctx = pdpSource(); fb = pdpFbtFor(form); }
      else if (form.closest && form.closest('quick-add-modal') && modalFbt && ctx === modalSource) { fb = modalFbt; fb.via = 'modal'; }
      if (fb) fb.attempt = String(++attemptSeq);
      pending.push({ vid: String(idEl && idEl.value || ''), src: ctx, qty: Number(qEl && qEl.value) || 1, at: now(), fbt: fb });
      if (pending.length > 10) pending.shift();
      return;
    }

    if (isCheckoutForm(form, submitter)) handOffToCheckout(submitter, form);
  }

  function onNotificationOpen() {
    var n = document.querySelector('cart-notification');
    var vid = n && n.dataset ? n.dataset.variantId : '';
    var p = takePending(vid);
    if (p) commitAdd(p.src, vid || p.vid, p.qty, p.fbt);
  }

  function onCartUpdated(e) {
    var d = (e && e.detail) || {};
    if (d.source === 'arb') {
      if (Number(d.itemsAdded) > 0) commitAdd('arb', '', Number(d.itemsAdded));
      return;
    }
    if (d.source === 'product-form') {
      var p = takePending('');
      if (p) commitAdd(p.src, p.vid, p.qty, p.fbt);
    }
  }

  // ── what led to a product page ───────────────────────────────────────────
  function productHandleFromHref(href) {
    var m = String(href || '').match(/\/products\/([^/?#]+)/);
    return m ? decodePart(m[1]) : '';
  }

  function rememberVia(handle, ctx) {
    if (!handle || !ctx) return;
    var m = readJSON(sessionStorage, VIA_KEY) || {};
    m[handle] = ctx;
    var keys = Object.keys(m);
    if (keys.length > 20) delete m[keys[0]];
    writeJSON(sessionStorage, VIA_KEY, m);
  }

  var viaCache = null;
  function viaForThisProduct() {
    if (viaCache !== null) return viaCache;
    var handle = productHandleFromHref(location.pathname);
    var m = readJSON(sessionStorage, VIA_KEY) || {};
    viaCache = clip((handle && m[handle]) || '', 30);
    return viaCache;
  }

  // ── page recording ────────────────────────────────────────────────────────
  var INFO_PAGES = [
    [/shipping|الشحن|التوصيل/, 'shipping'], [/return|exchange|refund|cancel|استرجاع|استبدال|الغاء/, 'policy'],
    [/story|حكاية|من-نحن|about/, 'story'], [/feedback|الفيدباك|تجارب/, 'reviews'],
    [/question|faq|اسئلة|أسئلة/, 'faq'], [/contact|تواصل/, 'contact'], [/brands|الماركات/, 'brands']
  ];

  function recordPage() {
    var path = cleanPath(location.pathname);
    state.pagesCount = (state.pagesCount || 0) + 1;
    state.pages = pushUniqueTail(state.pages, path, MAX_PAGES);
    if (!state.landingPath) state.landingPath = path;
    var pm = path.match(/^\/products\/(.+)$/);
    if (pm) state.products = pushUniqueTail(state.products, pm[1], MAX_PRODUCTS);
    var cm = path.match(/^\/collections\/([^/]+)/);
    if (cm && cm[1] !== 'all') state.collections = pushUniqueTail(state.collections, cm[1], MAX_COLLECTIONS);
    if (path.indexOf('/pages/') === 0) {
      for (var i = 0; i < INFO_PAGES.length; i++) {
        if (INFO_PAGES[i][0].test(path)) { state.info = pushUnique(state.info, INFO_PAGES[i][1]); break; }
      }
    }
    if (path.indexOf('/search') === 0) {
      var q = readQuery();
      var term = q ? (q.get('q') || q.get('query') || '').trim() : '';
      if (term) state.searches = pushUniqueTail(state.searches, clip(term, 40), MAX_SEARCHES);
    }
    if (!state.utm) {
      var qs = readQuery();
      if (qs) {
        var source = qs.get('utm_source') || (qs.get('fbclid') ? 'fb' : '');
        var parts = [source, qs.get('utm_medium') || '', qs.get('utm_campaign') || qs.get('campaign_id') || ''].filter(Boolean);
        if (parts.length) state.utm = clip(parts.join('|'), MAX_ATTR);
      }
    }
    save();
  }

  // ── section exposure (≥ half of the section, or of the screen, visible) ──
  var seenSections = {};
  var sectionIO = null;

  function markSection(name) {
    if (!name || seenSections[name]) return;
    seenSections[name] = true;
    state.sections = pushUnique(state.sections, name);
    save();
    publish('sondos_section_view', { section: name });
  }

  function sectionName(el) {
    var id = el.id.replace('shopify-section-', '');
    var cut = id.lastIndexOf('__');
    return clip(cut > -1 ? id.slice(cut + 2) : id, 40);
  }

  function observeSections() {
    if (!('IntersectionObserver' in window)) return;
    if (!sectionIO) {
      sectionIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var rb = entry.rootBounds;
          var need = 0.5 * Math.min(entry.boundingClientRect.height, rb ? rb.height : window.innerHeight);
          if (entry.intersectionRect.height < need || need <= 0) return;
          markSection(entry.target.getAttribute('data-sji-section'));
          sectionIO.unobserve(entry.target);
        });
      }, { threshold: [0, 0.25, 0.5, 0.75, 1] });
    }
    var nodes = document.querySelectorAll('[id^="shopify-section-"]');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.hasAttribute('data-sji-section')) continue;
      if (/header|announcement|footer|newsletter/i.test(el.id)) continue;
      var name = sectionName(el);
      el.setAttribute('data-sji-section', name);
      sectionIO.observe(el);
    }
  }

  // ── AI cart reco exposure (the visible card, once per card per page) ─────
  var recoIO = null;
  var recoSeen = {};

  function observeReco() {
    if (!('IntersectionObserver' in window)) return;
    if (!recoIO) {
      recoIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.intersectionRatio < 0.5) return;
          var card = entry.target;
          var pos = card.getAttribute('data-sc-reco-position') || '';
          recoIO.unobserve(card);
          if (recoSeen[pos]) return;
          recoSeen[pos] = true;
          state.recoSeen = true;
          save();
          var btn = card.querySelector('button.sondos-sc__reco-add');
          publish('sondos_reco', { action: 'view', position: pos, variant_id: String(btn && btn.value || '') });
        });
      }, { threshold: [0.5] });
    }
    var cards = document.querySelectorAll('[data-sc-reco-card]');
    for (var i = 0; i < cards.length; i++) {
      if (!cards[i].hidden && cards[i].getClientRects().length) recoIO.observe(cards[i]);
    }
  }

  // ── «مكمّلات» v6 exposure: a card ≥ 50 % visible, once per card per page ──
  var fbtIO = null;
  var fbtSeenNow = {};
  var fbtClicked = {};

  function noteFbtSeen(f) {
    // order attribute fbt_seen: "anchor@run8:highest visible slot seen", last 6 pages of the visit
    var key = f.anchor + '@' + String(f.run).slice(0, 8);
    var list = (state.fbtSeen || []).filter(function (x) { return x.split(':')[0] !== key; });
    var prev = (state.fbtSeen || []).filter(function (x) { return x.split(':')[0] === key; })[0];
    var top = Math.max(Number(prev ? prev.split(':')[1] : 0) || 0, Number(f.slot) || 0);
    list.push(key + ':' + top);
    state.fbtSeen = list.slice(-6);
  }

  function observeFbt() {
    if (!('IntersectionObserver' in window)) return;
    if (!fbtIO) {
      fbtIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.intersectionRatio < 0.5) return;
          fbtIO.unobserve(entry.target);
          var f = fbtOf(entry.target);
          if (!f) return;
          var k = f.run + '|' + f.anchor + '|' + f.slot;
          if (fbtSeenNow[k]) return;
          fbtSeenNow[k] = true;
          noteFbtSeen(f);
          save();
          publish('sondos_fbt', fbtPayload('view', f, {}));
        });
      }, { threshold: [0.5] });
    }
    var cards = document.querySelectorAll('.spc[data-spc-anchor] [data-spc-slot]');
    for (var i = 0; i < cards.length; i++) {
      if (cards[i].hasAttribute('data-sji-fbt')) continue;
      cards[i].setAttribute('data-sji-fbt', '');
      fbtIO.observe(cards[i]);
    }
  }

  // ── promo popup ───────────────────────────────────────────────────────────
  function watchPromo() {
    var modal = document.getElementById('snd-modal');
    if (!modal || typeof MutationObserver === 'undefined') return;
    var check = function () {
      if (!state.promoSeen && modal.classList.contains('show')) {
        state.promoSeen = true;
        save();
        publish('sondos_promo', { action: 'view' });
      }
    };
    check();
    new MutationObserver(check).observe(modal, { attributes: true, attributeFilter: ['class'] });
  }

  // ── clicks (all observation; nothing is prevented) ───────────────────────
  var ARB_STEPS = [
    ['[data-arb-start]', 'start'], ['[data-skin]', 'skin'], ['[data-concern]', 'concern'],
    ['[data-arb-shuffle]', 'shuffle'], ['[data-arb-swap]', 'swap'], ['[data-arb-add]', 'add'],
    ['[data-arb-restart]', 'restart'], ['[data-arb-back]', 'back'], ['[data-arb-howto-open]', 'howto']
  ];
  var ARB_ORDER = ['start', 'skin', 'concern', 'routine', 'add'];
  var videoTapped = [];

  function arbProgress(step) {
    if (ARB_ORDER.indexOf(step) > ARB_ORDER.indexOf(state.arbFurthest || '')) state.arbFurthest = step;
  }

  function onClickCapture(e) {
    var t = e.target;
    if (!t || !t.closest) return;

    // ARB steps
    for (var i = 0; i < ARB_STEPS.length; i++) {
      var hit = t.closest(ARB_STEPS[i][0]);
      if (!hit) continue;
      var step = ARB_STEPS[i][1];
      var value = step === 'skin' ? hit.getAttribute('data-skin') : step === 'concern' ? hit.getAttribute('data-concern') : '';
      if (step === 'start') state.arbStarted = true;
      arbProgress(step);
      save();
      publish('sondos_arb', { step: step, value: value || '' });
      return;
    }

    // Add More
    if (t.closest('a[data-sc-addons]')) {
      state.addMoreOpened = (state.addMoreOpened || 0) + 1;
      save();
      publish('sondos_add_more', { action: 'open' });
      return;
    }
    var tab = t.closest('dialog.sca .sca__tab');
    if (tab) {
      publish('sondos_add_more', { action: 'tab', tab: clip((tab.textContent || '').replace(/\s+/g, ' ').trim(), 24) });
      return;
    }

    // AI cart reco swap
    if (t.closest('[data-sc-reco-swap]')) {
      publish('sondos_reco', { action: 'swap' });
      setTimeout(observeReco, 60);
      return;
    }

    // Quick-add modal: remember which card opened it
    var opener = t.closest('modal-opener, .sdc__btn--options');
    if (opener) { modalSource = contextOf(opener); modalFbt = opener.closest('.spc') ? fbtOf(opener) : null; }

    // «اشتري الآن» (dynamic checkout): count the add here
    var buyNow = t.closest('.shopify-payment-button__button');
    if (buyNow) {
      var pf = buyNow.closest('form[action*="/cart/add"]');
      if (!pf) { var host = buyNow.closest('product-form, .product-form'); pf = host && host.querySelector('form[action*="/cart/add"]'); }
      var bid = pf && pf.querySelector('[name="id"]');
      var bfb = pageType === 'product' ? pdpFbtFor(pf) : null;
      if (bfb) { bfb.via = 'pdp_buy_now'; bfb.attempt = String(++attemptSeq); }
      commitAdd('buy_now' + (viaForThisProduct() ? '>' + viaForThisProduct() : ''), bid && bid.value, 1, bfb);
      return;
    }

    // Video: a deliberate tap (the shelf autoplays muted, so 'play' is not engagement)
    var vid = t.closest('[data-svs-expand], [data-sondos-pdp-toggle], [data-sondos-pdp-video]');
    if (vid) {
      var vkey = vid.closest('[data-svs-card], [data-sondos-pdp-video]') || vid;
      if (videoTapped.indexOf(vkey) > -1) return;
      videoTapped.push(vkey);
      var shelf = !!vid.closest('[data-svs]');
      var vcard = vid.closest('[data-svs-card]');
      var vtitle = vcard && vcard.querySelector('.svs__title');
      publish('sondos_video', { where: shelf ? 'shelf' : 'pdp', title: shelf ? clip((vtitle && vtitle.textContent || '').trim(), 50) : productHandleFromHref(location.pathname) });
      return;
    }

    // WhatsApp (MooseDesk floating button or any wa link)
    var wa = t.closest('#md-wa-app, a[href*="wa.me"], a[href*="api.whatsapp"], a[href*="whatsapp.com"]');
    if (wa) {
      var comp = wa.id === 'md-wa-app' || wa.closest('#md-wa-app') ? 'floating' : contextOf(wa) || pageType;
      state.waClicked = true;
      save();
      publish('sondos_wa', { component: comp });
      return;
    }

    // Promo code copy
    if (t.closest('#snd-ticket')) { publish('sondos_promo', { action: 'copy' }); return; }

    // Collection tab switch (client-side swap)
    var ctab = t.closest('a.sch__nav-link[data-sct-link]');
    if (ctab) {
      var cm = (ctab.getAttribute('href') || '').match(/\/collections\/([^/?#]+)/);
      if (cm) {
        state.collections = pushUniqueTail(state.collections, decodePart(cm[1]), MAX_COLLECTIONS);
        save();
        publish('sondos_collection_tab', { collection: decodePart(cm[1]) });
      }
      return;
    }

    // Product / collection links: where discovery happened
    var link = t.closest('a[href*="/products/"], a[href*="/collections/"]');
    if (link) {
      var ctx = contextOf(link);
      var handle = productHandleFromHref(link.getAttribute('href'));
      // «مكمّلات»: via / section keep the short label (complementary:<slot>); run + anchor go in their own event
      var fbc = ctx.indexOf('complementary:') === 0 ? fbtOf(link) : null;
      if (fbc) { ctx = ctx.split('@')[0]; rememberFbtCtx(handle, fbc); }
      if (handle) rememberVia(handle, ctx);
      publish('sondos_section_click', { section: ctx, target: clip(handle || cleanPath(link.getAttribute('href')), 60) });
      if (fbc && !fbtClicked[fbc.view_id + '|' + fbc.slot]) {
        fbtClicked[fbc.view_id + '|' + fbc.slot] = true;
        publish('sondos_fbt', fbtPayload('click', fbc, {}));
      }
    }
  }

  function onChangeCapture(e) {
    var t = e.target;
    if (t && t.matches && t.matches('[data-sc-zone-select]')) {
      var opt = t.options && t.options[t.selectedIndex];
      state.zone = clip((opt && (opt.getAttribute('data-label') || opt.textContent) || t.value || '').replace(/\s+/g, ' ').trim(), 40);
      save();
      publish('sondos_zone', { zone: state.zone });
    }
  }

  function onArbCompleted(e) {
    var d = (e && e.detail) || {};
    state.arbStarted = true;
    state.arbUsed = true;
    state.arbUses = (state.arbUses || 0) + 1;
    if (d.skinLabel || d.concernLabel || d.skin || d.concern) {
      state.arbAnswers = clip((d.skinLabel || d.skin || '') + ' · ' + (d.concernLabel || d.concern || ''), MAX_ATTR);
    }
    if (d.products && d.products.length) state.arbRoutine = clip(d.products.join(' › '), MAX_ATTR);
    arbProgress('routine');
    save();
    publish('sondos_arb', { step: 'routine', skin: d.skin || '', concern: d.concern || '', items: (d.products || []).length });
  }

  // ── checkout hand-off ─────────────────────────────────────────────────────
  // Shopify's cart form saves `attributes[...]` in the same POST /cart that
  // redirects to checkout, so the summary is written into the form as hidden
  // fields, synchronously. Nothing here waits or cancels.
  function buildPayload() {
    var f = readJSON(localStorage, FIRST_KEY) || {};
    var mins = Math.max(0, Math.round((state.lastAt - state.startedAt) / 60000));
    var d = new Date(state.startedAt);
    return {
      visit_minutes: String(mins),
      landing_path: clip(state.landingPath || '', MAX_ATTR),
      utm: clip(state.utm || '', MAX_ATTR),
      device: clip(state.device || '', 16),
      pages_viewed: clip((state.pages || []).join(' › '), MAX_ATTR),
      pages_count: String(state.pagesCount || 0),
      products_viewed: clip((state.products || []).join(' › '), MAX_ATTR),
      collections_viewed: clip((state.collections || []).join(' › '), MAX_ATTR),
      search_q: clip((state.searches || []).join(' › '), MAX_ATTR),
      sections_seen: clip((state.sections || []).join(', '), MAX_ATTR),
      info_seen: clip((state.info || []).join(', '), 80),
      arb_started: state.arbStarted ? 'نعم' : 'لا',
      arb_used: state.arbUsed ? 'نعم' : 'لا',
      arb_uses: String(state.arbUses || 0),
      arb_furthest: state.arbFurthest || '',
      arb_answers: clip(state.arbAnswers || '', MAX_ATTR),
      arb_routine: clip(state.arbRoutine || '', MAX_ATTR),
      added_to_cart: String(state.atc || 0),
      add_source: clip((state.addSources || []).join(', '), MAX_ATTR),
      add_src: srcAttribute(),
      time_to_atc_min: state.timeToAtcMin === '' || state.timeToAtcMin == null ? '' : String(state.timeToAtcMin),
      add_more_opened: String(state.addMoreOpened || 0),
      reco_seen: state.recoSeen ? 'نعم' : 'لا',
      fbt_seen: clip((state.fbtSeen || []).join(' '), MAX_ATTR),
      zone: clip(state.zone || '', 40),
      promo_seen: state.promoSeen ? 'نعم' : 'لا',
      wa_clicked: state.waClicked ? 'نعم' : 'لا',
      first_seen: f.at ? new Date(f.at).toISOString().slice(0, 10) + (f.utm ? ' · ' + clip(f.utm, 60) : '') : '',
      visit_no: String(f.visits || 1),
      journey_id: (state.id || '') + '@' + d.toISOString().slice(0, 16),
      tz: clip(readTimeZone(), 40),
      langs: clip(readLanguages(), 60),
      // v2 keys retired 2026-10-11 — sent blank so stale values do not linger.
      market: '',
      last_action: ''
    };
  }

  function isCartForm(form) {
    if (!form || form.tagName !== 'FORM') return false;
    var cartPath = ((window.routes && window.routes.cart_url) || '/cart').replace(/\/+$/, '');
    try {
      var action = new URL(form.getAttribute('action') || '', location.href);
      return action.origin === location.origin && action.pathname.replace(/\/+$/, '') === cartPath;
    } catch (e) { return false; }
  }

  function isCheckoutControl(el) {
    return el && el.closest ? el.closest('[name="checkout"], #checkout, #CartDrawer-Checkout, a[href*="/checkout"]') : null;
  }

  function isCheckoutForm(form, submitter) {
    return isCartForm(form) && (!submitter || isCheckoutControl(submitter));
  }

  function writeFormAttributes(form) {
    var payload = buildPayload();
    Object.keys(payload).forEach(function (key) {
      var input = form.querySelector('input[data-sji-attr="' + key + '"]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'attributes[' + key + ']';
        input.setAttribute('data-sji-attr', key);
        form.appendChild(input);
      }
      input.value = payload[key];
    });
  }

  // A link to /checkout cannot carry form fields: one keepalive request that
  // navigation never waits for.
  function sendWithoutWaiting() {
    if (!window.fetch) return;
    fetch((window.routes && window.routes.cart_update_url) || '/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: buildPayload() })
    }).catch(function () {});
  }

  function handOffToCheckout(control, form) {
    if (!consentOk()) return;
    try {
      if (isCartForm(form)) writeFormAttributes(form);
      else if (control && control.tagName === 'A') sendWithoutWaiting();
    } catch (err) { /* never stand between the customer and checkout */ }
  }

  // `click`, never `pointerdown`: on a phone every scroll starts with a
  // pointerdown and the sticky checkout dock sits under the thumb (owner
  // report, 2026-09-16). The click runs before the form submits.
  function onCheckoutClick(e) {
    var control = isCheckoutControl(e.target);
    if (!control) return;
    handOffToCheckout(control, control.form || (control.closest && control.closest('form')));
  }

  // ── init ──────────────────────────────────────────────────────────────────
  function init() {
    pageType = readPageType();
    state = load();
    recordPage();
    firstTouch();
    observeSections();
    observeReco();
    observeFbt();
    watchPromo();
    if (pageType === 'product') {
      pdpFbt = bindPdpFbt();
      var via = viaForThisProduct();
      if (!via) {
        var ref = document.referrer;
        var internal = false;
        try { internal = !!ref && new URL(ref).origin === location.origin; } catch (e) {}
        viaCache = internal ? '' : (state.utm ? 'ad' : 'external');
      }
    }

    var cap = { capture: true, passive: true };
    document.addEventListener('submit', onSubmitCapture, cap);
    document.addEventListener('click', onClickCapture, cap);
    document.addEventListener('click', onCheckoutClick, cap);
    document.addEventListener('change', onChangeCapture, cap);
    document.addEventListener('cart:notification-open', onNotificationOpen, { passive: true });
    document.addEventListener('cart:updated', onCartUpdated, { passive: true });
    document.addEventListener('sondos:arb:completed', onArbCompleted, { passive: true });
    document.addEventListener('sondos:section:hydrated', function () { observeSections(); }, { passive: true });
    document.addEventListener('sondos:fbt:loaded', function () { observeFbt(); }, { passive: true });
    window.addEventListener('load', flushPublishQueue, { once: true });
    setTimeout(flushPublishQueue, 4000);
  }

  function safeInit() {
    try { if (consentOk()) init(); } catch (e) { /* tracking must never break the page */ }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', safeInit, { once: true });
  else safeInit();
})();
